import React, { useCallback, useEffect, useState } from 'react';
import GeographyPreviewMap from './GeographyPreviewMap';
import {
  activateManagedLayerVersion,
  createManagedLayer,
  getManagedLayer,
  getManagedLayers,
  getManagedVersionFields,
  updateManagedLayer,
  updateManagedVersionFieldMapping,
  uploadManagedLayerVersion,
} from '../../services/geographyService';
import './GeographyLayersTab.css';

const AUDIENCES = [
  ['platform_staff', 'Platform staff'],
  ['tenant_staff', 'Community staff'],
  ['tenant_members', 'Community members'],
  ['citizens', 'Citizens'],
];

const MODULES = [
  ['harvesting', 'Harvesting'],
  ['lenses', 'Lenses'],
  ['assessment', 'Assessment'],
  ['land_use', 'Land use'],
  ['consultation', 'Consultation'],
  ['species_stewardship', 'Species / stewardship'],
];

const emptyCreate = {
  layerId: '', displayName: '', layerType: 'management_area', description: '', scope: 'tenant',
};

const emptyUpload = {
  sourceName: '', sourceUrl: '', licence: '', effectiveDate: '', sourceCrs: 'auto',
  idField: '', nameField: '', typeField: '', propertyExclude: '', simplifyTolerance: '0.001',
};

function splitList(value) {
  return String(value || '').split(',').map((entry) => entry.trim()).filter(Boolean);
}

function fieldLabel(field) {
  return field.samples?.length ? `${field.name} — e.g. ${field.samples.map((sample) => `"${sample}"`).join(', ')}` : field.name;
}

const NOTE_PREVIEW_LENGTH = 160;
const NOTE_COLLAPSED_LENGTH = 600;

function truncateText(text, length) {
  const value = String(text || '');
  return value.length > length ? `${value.slice(0, length).trimEnd()}…` : value;
}

function NoteItem({ text }) {
  const [showAll, setShowAll] = useState(false);
  const isLong = text.length > NOTE_COLLAPSED_LENGTH;
  return (
    <li>
      {showAll ? text : truncateText(text, NOTE_COLLAPSED_LENGTH)}
      {isLong && (
        <button type="button" className="geo-layers__link-button" onClick={() => setShowAll((current) => !current)}>
          {showAll ? 'Show less' : `Show all (${text.length.toLocaleString()} characters)`}
        </button>
      )}
    </li>
  );
}

export function ValidationNotes({ messages, tone = 'warning', defaultOpen = false }) {
  const list = (messages || []).filter(Boolean);
  if (!list.length) return null;
  const label = tone === 'error' ? 'Error' : 'Note';
  return (
    <details className={`geo-layers__notes geo-layers__notes--${tone}`} open={defaultOpen}>
      <summary>
        <strong>{list.length === 1 ? label : `${list.length} ${label.toLowerCase()}s`}:</strong>{' '}
        {truncateText(list[0], NOTE_PREVIEW_LENGTH)}
      </summary>
      <ul>
        {list.map((entry, index) => <NoteItem key={index} text={entry} />)}
      </ul>
    </details>
  );
}

function VersionNaming({ layerId, version, busy, onSaved, onError }) {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState(null);
  const [mapping, setMapping] = useState({ nameField: '', typeField: '' });
  const [saving, setSaving] = useState(false);

  const openPanel = async () => {
    setOpen(true);
    setMapping({
      nameField: version.fieldMapping?.nameField || '',
      typeField: version.fieldMapping?.typeField || '',
    });
    if (fields) return;
    try {
      const response = await getManagedVersionFields(layerId, version._id);
      setFields(response.data.fields || []);
    } catch (error) {
      setFields([]);
      onError(error.response?.data?.message || 'Could not load the fields for this version.');
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateManagedVersionFieldMapping(layerId, version._id, mapping);
      setOpen(false);
      await onSaved(version, mapping);
    } catch (error) {
      onError(error.response?.data?.message || 'Could not update feature names.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={openPanel} disabled={busy}>
        {version.fieldMapping?.nameField ? `Names: ${version.fieldMapping.nameField}` : 'Choose name field'}
      </button>
    );
  }

  return (
    <div className="geo-layers__naming">
      {fields === null ? <span className="geo-layers__muted">Loading fields…</span> : (
        <>
          <label>
            Name features by
            <select value={mapping.nameField} onChange={(event) => setMapping({ ...mapping, nameField: event.target.value })}>
              <option value="">Numbered (Feature 1, Feature 2…)</option>
              {fields.map((field) => <option key={field.name} value={field.name}>{fieldLabel(field)}</option>)}
            </select>
          </label>
          <label>
            Category field (optional)
            <select value={mapping.typeField} onChange={(event) => setMapping({ ...mapping, typeField: event.target.value })}>
              <option value="">None</option>
              {fields.map((field) => <option key={field.name} value={field.name}>{fieldLabel(field)}</option>)}
            </select>
          </label>
          <div className="geo-layers__actions">
            <button type="button" onClick={save} disabled={saving || busy}>{saving ? 'Saving…' : 'Apply names'}</button>
            <button type="button" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
          </div>
        </>
      )}
    </div>
  );
}

function ToggleGroup({ options, values, onChange }) {
  const selected = new Set(values || []);
  return (
    <div className="geo-layers__checks">
      {options.map(([value, label]) => (
        <label key={value}>
          <input
            type="checkbox"
            checked={selected.has(value)}
            onChange={() => onChange(selected.has(value)
              ? [...selected].filter((entry) => entry !== value)
              : [...selected, value])}
          />
          {label}
        </label>
      ))}
    </div>
  );
}

export default function GeographyLayersTab({ tenantId, isPlatformStaff = false }) {
  const [layers, setLayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [versions, setVersions] = useState([]);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [settings, setSettings] = useState(null);
  const [uploadForm, setUploadForm] = useState(emptyUpload);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const loadLayers = useCallback(async () => {
    const response = await getManagedLayers(tenantId);
    setLayers(response.data || []);
  }, [tenantId]);

  const openLayer = useCallback(async (layerId) => {
    const response = await getManagedLayer(layerId);
    setSelected(response.data.layer);
    setVersions(response.data.versions || []);
    setSettings({
      displayName: response.data.layer.displayName,
      description: response.data.layer.description || '',
      layerType: response.data.layer.layerType,
      scope: response.data.layer.scope,
      enabled: !!response.data.layer.enabled,
      audiences: response.data.layer.audiences || [],
      publishedTenantIds: (response.data.layer.publishedTenantIds || []).map(String),
      modules: response.data.layer.modules || [],
    });
    setPreview(null);
  }, []);

  useEffect(() => {
    loadLayers().catch((error) => setMessage(error.response?.data?.message || 'Could not load geography layers.'));
  }, [loadLayers]);

  const refresh = async (layerId = selected?._id) => {
    await loadLayers();
    if (layerId) await openLayer(layerId);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setMessage('');
    try {
      const response = await createManagedLayer({
        ...createForm,
        tenantId: createForm.scope === 'tenant' ? tenantId : undefined,
        modules: [],
      });
      setCreateForm(emptyCreate);
      setShowCreate(false);
      await refresh(response.data._id);
      setMessage('Layer created. Upload a validated dataset version before enabling it.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Layer creation failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSettings = async () => {
    setBusy(true);
    setMessage('');
    try {
      const payload = { ...settings };
      if (!isPlatformStaff || payload.scope === selected.scope) {
        delete payload.scope;
      } else if (payload.scope === 'tenant') {
        payload.tenantId = tenantId;
        delete payload.audiences;
        delete payload.publishedTenantIds;
      } else {
        delete payload.audiences;
        delete payload.publishedTenantIds;
      }
      await updateManagedLayer(selected._id, payload);
      await refresh(selected._id);
      setMessage('Layer metadata and availability settings saved.');
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not save layer settings.');
    } finally {
      setBusy(false);
    }
  };

  const loadPreview = async (layerId, versionId) => {
    const response = await getManagedLayer(layerId);
    const version = response.data.versions.find((entry) => String(entry._id) === String(versionId));
    if (!version) throw new Error('Preview version not found.');
    setPreview({ layerId, version, loadedAt: Date.now() });
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setMessage('Uploading and checking the file. Large layers may take several minutes.');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('sourceName', uploadForm.sourceName);
    formData.append('sourceUrl', uploadForm.sourceUrl);
    formData.append('licence', uploadForm.licence);
    formData.append('effectiveDate', uploadForm.effectiveDate);
    formData.append('sourceCrs', uploadForm.sourceCrs);
    formData.append('simplifyTolerance', uploadForm.simplifyTolerance);
    formData.append('fieldMapping', JSON.stringify({
      idField: uploadForm.idField.trim(),
      nameField: uploadForm.nameField.trim(),
      typeField: uploadForm.typeField.trim(),
    }));
    formData.append('propertyExclude', JSON.stringify(splitList(uploadForm.propertyExclude)));
    try {
      const response = await uploadManagedLayerVersion(selected._id, formData);
      const version = response.data;
      setFile(null);
      setUploadForm(emptyUpload);
      await refresh(selected._id);
      await loadPreview(selected._id, version._id).catch(() => {});
      const named = version.fieldMapping?.nameField
        ? `Features are named by "${version.fieldMapping.nameField}"`
        : 'Features are numbered';
      setMessage(`Version ${version.sequence} uploaded (${version.featureCount.toLocaleString()} features). ${named} — use "Choose name field" to change it, then activate.`);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Upload failed. The active version was not changed.');
    } finally {
      setBusy(false);
    }
  };

  const handleActivate = async (version, rollback) => {
    setBusy(true);
    setMessage('');
    try {
      await activateManagedLayerVersion(
        selected._id,
        version._id,
        selected.activationRevision,
        rollback
      );
      await refresh(selected._id);
      setMessage(rollback ? `Rolled back to version ${version.sequence}.` : `Version ${version.sequence} is now active.`);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Version activation failed.');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async (version) => {
    setBusy(true);
    try {
      await loadPreview(selected._id, version._id);
    } catch (error) {
      setMessage(error.response?.data?.message || 'Could not load the map preview.');
    } finally {
      setBusy(false);
    }
  };

  const handleNamesSaved = async (version, mapping) => {
    await refresh(selected._id);
    await loadPreview(selected._id, version._id).catch(() => {});
    setMessage(mapping.nameField
      ? `Version ${version.sequence} features are now named by "${mapping.nameField}".`
      : `Version ${version.sequence} features are now numbered.`);
  };

  const closeLayer = () => {
    setSelected(null);
    setVersions([]);
    setSettings(null);
    setPreview(null);
  };

  const activeVersionId = selected?.activeVersionId?._id || selected?.activeVersionId;

  return (
    <div className="geo-layers">
      <div className="section-header">
        <div>
          <h3>Geography Layers</h3>
          <p className="geo-layers__muted">Manage validated datasets used by CIP maps, lenses, assessments and citizen workflows.</p>
        </div>
      </div>

      {message && <div className="geo-layers__message">{message}</div>}

      <details className="geo-layers__new" open={showCreate} onToggle={(event) => setShowCreate(event.currentTarget.open)}>
        <summary>+ New layer</summary>
        <form className="geo-layers__create" onSubmit={handleCreate}>
          <label>
            Stable layer ID
            <input placeholder="e.g. ontario_wmu" value={createForm.layerId} onChange={(event) => setCreateForm({ ...createForm, layerId: event.target.value })} required />
            <small>A short permanent code (letters, numbers, underscores). Other parts of CIP refer to the layer by it, so it should not change after creation.</small>
          </label>
          <label>
            Display name
            <input placeholder="e.g. Wildlife Management Units" value={createForm.displayName} onChange={(event) => setCreateForm({ ...createForm, displayName: event.target.value })} required />
            <small>What people see in the app. You can rename it later.</small>
          </label>
          <label>
            Type / category
            <input placeholder="e.g. management_area" value={createForm.layerType} onChange={(event) => setCreateForm({ ...createForm, layerType: event.target.value })} required />
            <small>Groups similar layers, e.g. wildlife_management_unit or fisheries_management_zone.</small>
          </label>
          {isPlatformStaff && (
            <label>
              Who owns it
              <select value={createForm.scope} onChange={(event) => setCreateForm({ ...createForm, scope: event.target.value })}>
                <option value="tenant">Community layer</option>
                <option value="platform">Platform layer</option>
              </select>
              <small>Community: belongs to this community only. Platform: shared province-wide data that can be published to several communities.</small>
            </label>
          )}
          <button type="submit" disabled={busy}>Create layer</button>
        </form>
      </details>

      <div className="geo-layers__list-heading">
        <h4>Existing layers</h4>
        <span className="geo-layers__muted">Click a layer to open it.</span>
      </div>

      <div className="geo-layers__table-wrap">
        <table className="admin-table geo-layers__table">
          <thead>
            <tr><th>Layer</th><th>Type</th><th>Scope</th><th>Audience</th><th>Modules</th><th>Enabled</th><th>Active Version</th><th>Features</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {layers.map((layer) => (
              <tr key={layer._id} onClick={() => (selected?._id === layer._id ? closeLayer() : openLayer(layer._id))} className={selected?._id === layer._id ? 'row-selected' : ''}>
                <td><strong>{layer.displayName}</strong><small>{layer.layerId}</small></td>
                <td>{layer.layerType}</td>
                <td>{layer.scope}</td>
                <td>{(layer.audiences || []).join(', ') || 'None'}</td>
                <td>{(layer.modules || []).join(', ') || 'None'}</td>
                <td>{layer.enabled ? 'Yes' : 'No'}</td>
                <td>{layer.activeVersionId?.sequence ? `v${layer.activeVersionId.sequence}` : 'None'}</td>
                <td>{layer.activeVersionId?.featureCount?.toLocaleString?.() || 0}</td>
                <td>{new Date(layer.updatedAt).toLocaleDateString()}</td>
              </tr>
            ))}
            {!layers.length && <tr><td colSpan="9">No managed geography layers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && settings && (
        <div className="geo-layers__selected-bar">
          <div>
            <strong>{selected.displayName}</strong>
            <small>{selected.layerId}</small>
          </div>
          <button type="button" onClick={closeLayer}>✕ Close layer</button>
        </div>
      )}

      {selected && settings && (
        <div className="geo-layers__workspace">
          <section className="geo-layers__panel">
            <h4>Edit layer metadata</h4>
            <label>Name<input value={settings.displayName} onChange={(event) => setSettings({ ...settings, displayName: event.target.value })} /></label>
            <label>Type<input value={settings.layerType} onChange={(event) => setSettings({ ...settings, layerType: event.target.value })} /></label>
            <label>Description<textarea value={settings.description} onChange={(event) => setSettings({ ...settings, description: event.target.value })} /></label>
            {isPlatformStaff && (
              <label>
                Who owns it
                <select value={settings.scope} onChange={(event) => setSettings({ ...settings, scope: event.target.value })}>
                  <option value="tenant">Community layer</option>
                  <option value="platform">Platform layer</option>
                </select>
                <small>Changing this resets the audience to the default for that type. Save to apply.</small>
              </label>
            )}
            <label className="geo-layers__inline-check"><input type="checkbox" checked={settings.enabled} onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })} /> Enabled for CIP use</label>
            <h5>Audience / publication</h5>
            <ToggleGroup options={AUDIENCES} values={settings.audiences} onChange={(audiences) => setSettings({ ...settings, audiences })} />
            {selected.scope === 'platform' && (
              <label>
                Community publication scope
                <select
                  value={settings.publishedTenantIds.length ? 'current' : 'all'}
                  onChange={(event) => setSettings({
                    ...settings,
                    publishedTenantIds: event.target.value === 'current' ? [tenantId] : [],
                  })}
                >
                  <option value="current">Only this community</option>
                  <option value="all">All permitted communities</option>
                </select>
              </label>
            )}
            <h5>Module availability</h5>
            <ToggleGroup options={MODULES} values={settings.modules} onChange={(modules) => setSettings({ ...settings, modules })} />
            <button type="button" onClick={handleSaveSettings} disabled={busy}>Save metadata and availability</button>
          </section>

          <section className="geo-layers__panel">
            <h4>Upload new version</h4>
            <p className="geo-layers__muted">Choose a GeoJSON file up to 1 GiB (1,024 MiB) and upload it. The ID, name field and coordinate system are detected automatically — you can pick a different name field afterwards.</p>
            <form onSubmit={handleUpload} className="geo-layers__upload">
              <input type="file" accept=".json,.geojson,application/json,application/geo+json" onChange={(event) => setFile(event.target.files?.[0] || null)} required />
              <button type="submit" disabled={busy || !file}>{busy ? 'Working…' : 'Upload & preview'}</button>
              <details className="geo-layers__details">
                <summary>More details (optional)</summary>
                <div className="geo-layers__grid">
                  <label>Source / provenance<input value={uploadForm.sourceName} onChange={(event) => setUploadForm({ ...uploadForm, sourceName: event.target.value })} /></label>
                  <label>Source URL<input type="url" value={uploadForm.sourceUrl} onChange={(event) => setUploadForm({ ...uploadForm, sourceUrl: event.target.value })} /></label>
                  <label>Licence<input value={uploadForm.licence} onChange={(event) => setUploadForm({ ...uploadForm, licence: event.target.value })} /></label>
                  <label>Effective / version date<input type="date" value={uploadForm.effectiveDate} onChange={(event) => setUploadForm({ ...uploadForm, effectiveDate: event.target.value })} /></label>
                  <label>Coordinate system<select value={uploadForm.sourceCrs} onChange={(event) => setUploadForm({ ...uploadForm, sourceCrs: event.target.value })}><option value="auto">Detect from file</option><option value="EPSG:4326">WGS84 (EPSG:4326)</option><option value="EPSG:4269">NAD83 (EPSG:4269)</option><option value="EPSG:3857">Web Mercator (EPSG:3857)</option></select></label>
                  <label>Feature ID field<input value={uploadForm.idField} onChange={(event) => setUploadForm({ ...uploadForm, idField: event.target.value })} placeholder="Auto-detect" /></label>
                  <label>Feature name field<input value={uploadForm.nameField} onChange={(event) => setUploadForm({ ...uploadForm, nameField: event.target.value })} placeholder="Auto-detect" /></label>
                  <label>Category field<input value={uploadForm.typeField} onChange={(event) => setUploadForm({ ...uploadForm, typeField: event.target.value })} placeholder="None" /></label>
                </div>
                <label>Exclude fields<input value={uploadForm.propertyExclude} onChange={(event) => setUploadForm({ ...uploadForm, propertyExclude: event.target.value })} placeholder="Comma-separated fields to leave out (e.g. sensitive notes)" /></label>
              </details>
            </form>
          </section>

          {preview && (
            <section className="geo-layers__panel">
              <h4>Map preview <small>(local area only)</small></h4>
              <GeographyPreviewMap key={preview.loadedAt} layerId={preview.layerId} version={preview.version} />
            </section>
          )}

          <section className="geo-layers__panel geo-layers__versions">
            <h4>Versions</h4>
            {versions.map((version) => {
              const isActive = String(activeVersionId || '') === String(version._id);
              return (
                <div key={version._id} className="geo-layers__version">
                  <div><strong>Version {version.sequence}</strong> <span>{version.state}</span>{isActive && <span className="geo-layers__active">Active</span>}</div>
                  <small>{version.featureCount.toLocaleString()} features · {new Date(version.uploadedAt).toLocaleString()}</small>
                  <ValidationNotes messages={version.validation?.errors} tone="error" />
                  <ValidationNotes messages={version.validation?.warnings} tone="warning" defaultOpen={version.state === 'ready'} />
                  <div className="geo-layers__actions">
                    <button type="button" onClick={() => handlePreview(version)} disabled={busy || version.state !== 'ready'}>Preview map</button>
                    {version.state === 'ready' && (
                      <VersionNaming
                        layerId={selected._id}
                        version={version}
                        busy={busy}
                        onSaved={handleNamesSaved}
                        onError={setMessage}
                      />
                    )}
                    {!isActive && <button type="button" onClick={() => handleActivate(version, Boolean(activeVersionId))} disabled={busy || version.state !== 'ready'}>{activeVersionId ? 'Roll back / activate' : 'Activate'}</button>}
                  </div>
                </div>
              );
            })}
            {!versions.length && <p className="geo-layers__muted">No uploaded versions.</p>}
          </section>
        </div>
      )}


    </div>
  );
}